import { BUTLER_TAB } from '@cart-services/constants/butler-tab'
import { type ProjectCode, projectNameMap } from '@constants'
import { Container } from '@pets-ui/Container/Container'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { VStack } from '@pets-ui/VStack/VStack'
import { useMemo } from 'react'
import { useHistory } from 'react-router-dom-v5'
import { AiRecommendationFooter } from '../../../../fp-b2b-shared/ai-purchase-order/components/AiRecommendationFooter/AiRecommendationFooter'
import { AiRecommendationHeader } from '../../../../fp-b2b-shared/ai-purchase-order/components/AiRecommendationHeader/AiRecommendationHeader'
import { useButler } from '../../../../fp-b2b-shared/ai-purchase-order/components/ButlerProvider/useButler'
import { butlerTabIconMap, butlerTabLabelMap } from '../../../../fp-b2b-shared/ai-purchase-order/constants'
import { useStmQuickOrderStore } from '../../../hooks'
import { isCsvItemBlocked, isCsvItemPending, isCsvItemQuantityInvalid } from '../../../hooks/use-stm-quick-order-store'
import { useFastOrderBrands } from '../../hooks/use-fast-order-brands'
import { CsvBrandFilterChips } from './CsvBrandFilterChips'
import { CsvBrandSection } from './CsvBrandSection'
import { formatAddToCartLabel } from './format-add-to-cart-label'
import { getCsvCheckAllState } from './get-csv-check-all-state'
import { groupCsvItemsByBrand, listCsvBrands } from './group-csv-items-by-brand'
import { useCsvCartSubmission } from './use-csv-cart-submission'
import { useCsvItemsCheck } from './use-csv-items-check'

const CsvTabIcon = butlerTabIconMap[BUTLER_TAB.CSV]

export const CsvRecommendationContent = () => {
  const history = useHistory()
  const isMobile = useResponsive('mobile')
  const { routes } = useButler()
  const { fileName, items, brandFilter } = useStmQuickOrderStore(state => state.csv)
  const setCsvBrandFilter = useStmQuickOrderStore(state => state.setCsvBrandFilter)
  const upsertCsvItem = useStmQuickOrderStore(state => state.upsertCsvItem)
  const { fastOrderBrands } = useFastOrderBrands()
  const { hasPendingItems, hasCheckFailed, runCheck, retryCheck } = useCsvItemsCheck()
  const { submit, isSubmitting } = useCsvCartSubmission({ onRejected: runCheck })

  const groups = useMemo(() => groupCsvItemsByBrand(items), [items])
  const brands = useMemo(() => listCsvBrands(items), [items])
  const brandNameMap = useMemo<Partial<Record<ProjectCode, string>>>(
    () => ({ ...projectNameMap, ...Object.fromEntries(fastOrderBrands.map(brand => [brand.projectCode, brand.brandName])) }),
    [fastOrderBrands],
  )
  const visibleGroups = brandFilter ? groups.filter(group => group.projectCode === brandFilter) : groups
  const activeItems = items.filter(item => !item.isDeleted)
  const hasInvalidQuantity = activeItems.some(item => item.checked && isCsvItemQuantityInvalid(item))
  const submittableItems = activeItems.filter(item => item.checked && !isCsvItemBlocked(item) && !isCsvItemPending(item) && !isCsvItemQuantityInvalid(item))
  const orderTotal = submittableItems.reduce((total, item) => total + (item.check?.amount ?? item.quantity * item.product.unitPrice), 0)
  const checkAllState = getCsvCheckAllState(activeItems)

  const handleCheckAllChange = () => {
    activeItems.forEach((item) => {
      if (isCsvItemBlocked(item)) return
      upsertCsvItem(item.key, { checked: checkAllState.nextChecked })
    })
  }

  const handleAddToCarts = () => {
    submit(submittableItems)
  }

  // 失敗只對還在檢查中的商品有意義；數量改成 0 的商品不送檢查，沒有檢查中商品時回到一般狀態，由數量檢核擋送出
  const footer = hasCheckFailed && hasPendingItems
    ? { label: '重新檢查', disabled: false, onSubmit: retryCheck }
    : { label: hasPendingItems ? '重新檢查中' : formatAddToCartLabel(submittableItems), disabled: hasPendingItems || hasInvalidQuantity || !submittableItems.length, onSubmit: handleAddToCarts }

  return (
    <>
      <Container pb={18} display="flex" flexDirection="column" gap={4}>
        <AiRecommendationHeader
          onBack={() => history.push(routes.entry)}
          numberOfItems={submittableItems.length}
          orderTotal={orderTotal}
          source={(
            <>
              {CsvTabIcon && <CsvTabIcon />}
              <Text variant="subtitle">
                {isMobile ? butlerTabLabelMap[BUTLER_TAB.CSV] : `${butlerTabLabelMap[BUTLER_TAB.CSV]} : ${fileName}`}
              </Text>
            </>
          )}
        />
        <VStack gap={4} px={isMobile ? 4 : 0}>
          <CsvBrandFilterChips brands={brands} brandNameMap={brandNameMap} value={brandFilter} onChange={setCsvBrandFilter} />
          {visibleGroups.length
            ? visibleGroups.map(group => <CsvBrandSection key={group.projectCode} group={group} brandName={brandNameMap[group.projectCode] ?? group.projectCode} disabled={isSubmitting} />)
            : (
              <VStack flex={1} minHeight={320} justifyContent="center" alignItems="center">
                <Text variant="subtitle" color={colors.grey600}>沒有可加入的商品</Text>
              </VStack>
            )}
        </VStack>
      </Container>
      <AiRecommendationFooter
        allChecked={checkAllState.checked}
        checkAllIndeterminate={checkAllState.indeterminate}
        onCheckAllChange={handleCheckAllChange}
        onSubmit={footer.onSubmit}
        submitDisabled={footer.disabled}
        submitLabel={footer.label}
        submitLoading={isSubmitting}
      />
    </>
  )
}
